import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { Workspace } from './types';
import { ManageWorkspaceGeneral } from './manage/ManageWorkspaceGeneral';
import { ManageWorkspaceMembers } from './manage/ManageWorkspaceMembers';
import { Tabs } from '../common/ui/Tabs';
import BaseCard from '../common/layout/BaseCard';
import Pill from '../common/display/Pill';
import { captureAppException, createSupabaseError } from '../../utils/errorCaptureUtils';
import Breadcrumb from '../common/layout/Breadcrumb';
import { GlobeAltIcon, LockClosedIcon } from '@heroicons/react/24/outline';
// import { ManageWorkspaceDiscordInsights } from './discord/ManageWorkspaceDiscordInsights';

export function ManageWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(initialTab);

  const breadcrumbItems = [
    { label: `${workspace?.workspace_name || 'Workspace'} Workspace`, href: `/workspace/${id}` },
    { label: 'Manage' }
  ];

  const handleBreadcrumbNavigate = (item: any) => {
    if (item.href) navigate(item.href);
  };

  useEffect(() => {
    // Preserve existing search parameters when updating tab
    const currentParams = new URLSearchParams(location.search);
    currentParams.set('tab', activeTab);
    navigate(`?${currentParams.toString()}`, { replace: true });
  }, [activeTab, navigate, location.search]);

  useEffect(() => {
    async function fetchWorkspace() {
      try {
        const { data: workspaceData, error: workspaceError } = await supabase
          .from('workspaces')
          .select(
            `
            *,
            workspace_users (
              user_id,
              role,
              joined_at
            )
          `
          )
          .eq('workspace_id', id)
          .single();

        if (workspaceError) throw createSupabaseError(workspaceError, 'fetch_workspace');
        setWorkspace(workspaceData);
      } catch (err) {
        captureAppException(err, {
          tags: {
            context: 'manage_workspace_page',
            workspace_id: id,
            action: 'fetch_workspace'
          }
        });
        setError(
          err instanceof Error ? err.message : 'Failed to load workspace'
        );
      } finally {
        setLoading(false);
      }
    }
    fetchWorkspace();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <BaseCard>
        <div className="p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600">{error || 'Workspace not found'}</p>
        </div>
      </BaseCard>
    );
  }

  if (!workspace) {
    return (
      <BaseCard>
        <div className="p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Workspace not found
          </h2>
        </div>
      </BaseCard>
    );
  }

  const canManageWorkspace = (workspace: Workspace) => {
    if (!user) return false;
    const userRole = workspace.workspace_users.find(
      (u) => u.user_id === user.id
    )?.role;
    return userRole === 'owner' || userRole === 'editor';
  };

  if (!canManageWorkspace(workspace)) {
    return (
      <BaseCard>
        <div className="p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">
            You do not have permission to manage this workspace.
          </p>
        </div>
      </BaseCard>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', content: <ManageWorkspaceGeneral /> },
    { id: 'members', label: 'Members', content: <ManageWorkspaceMembers /> },
  ];

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="space-y-6">
      <BaseCard overflow='visible' className="pb-8">
        <div className="flex flex-col lg:flex-row lg:h-full">
          <div className="flex-1">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} onNavigate={handleBreadcrumbNavigate} theme="light" />
            </div>
            
            <div className="flex items-center gap-2 lg:mb-0 mb-2">
              <h1 className="text-h5 font-bold text-gray-900">
                Manage {workspace.workspace_name}
              </h1>
              <div className="hidden lg:flex items-center gap-2">
                <Pill
                  text={workspace.workspace_type === 'personal' ? 'Personal' : 'Team'}
                  className={
                    workspace.workspace_type === 'personal'
                      ? 'bg-secondary-100 text-secondary-base text-xs py-1'
                      : 'bg-primary-100 text-primary-base text-xs py-1'
                  }
                />
                <Pill
                  text={workspace.is_public ? 'Public' : 'Private'}
                  className={workspace.is_public 
                    ? "bg-green-100 text-green-700 flex items-center gap-1 text-xs py-1"
                    : "bg-blue-100 text-blue-700 flex items-center gap-1 text-xs py-1"
                  }
                  icon={workspace.is_public 
                    ? <GlobeAltIcon className="w-3 h-3" />
                    : <LockClosedIcon className="w-3 h-3" />
                  }
                />
              </div>
            </div>

            <div className="lg:hidden mb-3 flex items-center gap-2">
              <Pill
                text={workspace.workspace_type === 'personal' ? 'Personal' : 'Team'}
                className={
                  workspace.workspace_type === 'personal'
                    ? 'bg-secondary-100 text-secondary-base text-xs py-1'
                    : 'bg-primary-100 text-primary-base text-xs py-1'
                }
              />
              <Pill
                text={workspace.is_public ? 'Public' : 'Private'}
                className={workspace.is_public 
                  ? "bg-green-100 text-green-700 flex items-center gap-1 text-xs py-1"
                  : "bg-blue-100 text-blue-700 flex items-center gap-1 text-xs py-1"
                }
                icon={workspace.is_public 
                  ? <GlobeAltIcon className="w-3 h-3" />
                  : <LockClosedIcon className="w-3 h-3" />
                }
              />
            </div>
          </div>
        </div>
      </BaseCard>

      <div>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
        <div className="mt-6">
          {activeTabContent}
        </div>
      </div>
    </div>
  );
} 